/**
 * Consola de los modos A y C, y del registro de excepciones (#79, #80).
 *
 * La pantalla de `/activation` es el modo B: un lector acumula códigos y los
 * sube en tandas. Acá viven los otros dos mecanismos, que existen porque los
 * clientes tienen capacidades muy distintas y un solo modo no sirve:
 *
 *   **Modo A · por rango** — el productor sin automatización. "Rollo 47,
 *   series 1.000.000 a 1.010.000 → lote L2026-0801". La trampa del modo A es
 *   que supone que las etiquetas se aplican en orden y sin descarte, y eso la
 *   realidad lo rompe siempre. Por eso el formulario **obliga** a declarar las
 *   excepciones: o se listan los descartes, o alguien firma que no hubo. No
 *   hay tercera opción y no hay valor por defecto.
 *
 *   **Modo C · en línea** — el sistema de visión, unidad por unidad. Lo que
 *   esta tarjeta tiene que mostrar no es "activada" sino **cuánto tardó**: una
 *   línea a 300 unidades/minuto son 5 por segundo, y si la API es el cuello de
 *   botella la línea baja su velocidad.
 *
 * Y el registro de excepciones suelto, para lo que se descubre después de que
 * el turno terminó. Ojo: `VOIDED` es terminal y sólo se alcanza desde antes de
 * la activación. Una etiqueta ya activada no se anula desde acá — para eso
 * están el recall y la destrucción documentada.
 *
 * Las tres tarjetas son componentes de nivel superior y no funciones anidadas:
 * una función declarada dentro del cuerpo del padre es un componente NUEVO en
 * cada render, y React remonta el subárbol y pierde lo que el operario venía
 * escribiendo.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { activationKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import type { ApiError } from '@/api/errors';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import type { MessageKey } from '@/i18n/messages';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { OutcomeBreakdown } from './ActivationResult';
import { parseScan, shortCode, suggestBatchKey } from './codes';
import {
  median,
  outsideRange,
  parseRanges,
  rangeWidth,
  reasonLabelKey,
  VOID_REASONS,
  voidOutcomeLabelKey,
  voidOutcomeTone,
  type ExceptionBody,
  type ExceptionResult,
  type ExceptionSpec,
  type RangeBody,
  type RangeResult,
  type UnitBody,
  type UnitResult,
  type VoidReason,
} from './modes';

type Batch = components['schemas']['BatchOut'];

const BATCH_OPTIONS = 100;
/** Cuántas latencias del modo C se conservan para el p50 de la pantalla. */
const LATENCY_WINDOW = 30;

/** Cómo se declaran los descartes. `none` es una declaración, no un vacío. */
type ExceptionMode = 'none' | 'ranges' | 'codes';

const EXCEPTION_MODE_LABELS: Record<ExceptionMode, MessageKey> = {
  none: 'activation.exceptions.mode.none',
  ranges: 'activation.exceptions.mode.ranges',
  codes: 'activation.exceptions.mode.codes',
};

const FIELD =
  'mt-1 h-10 w-full rounded border border-border-strong bg-surface px-2 text-sm text-ink';
const AREA =
  'mt-1 h-24 w-full rounded border border-border-strong bg-surface px-2 py-2 font-mono text-sm text-ink';
const LABEL = 'text-xs tracking-wide text-ink-subtle uppercase';
const HINT = 'mt-1 block text-xs text-ink-subtle';

function toNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function ActivationModesPage(): ReactNode {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const batches = useKeysetQuery<Batch>(
    activationKeys.batchOptions(BATCH_OPTIONS),
    '/api/v1/batches',
    undefined,
    { limit: BATCH_OPTIONS },
  );

  const [batchId, setBatchId] = useState<number | null>(null);
  const [lineId, setLineId] = useState('LINEA-1');

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader
          title={t('activation.modes.title')}
          description={t('activation.modes.description')}
          actions={
            <Link
              to="/activation"
              className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-accent underline underline-offset-2"
            >
              {t('activation.modes.back')}
            </Link>
          }
        />
        <CardBody className="space-y-4">
          <p className="max-w-prose text-sm text-ink-muted">{t('activation.modes.rule')}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={LABEL}>{t('activation.field.batch')}</span>
              <select
                className={FIELD}
                value={batchId === null ? '' : String(batchId)}
                disabled={batches.query.isPending}
                onChange={(event) => {
                  const value = event.target.value;
                  setBatchId(value === '' ? null : Number.parseInt(value, 10));
                }}
              >
                <option value="">{t('activation.field.batchPlaceholder')}</option>
                {batches.items.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.lot_code}
                    {batch.status === 'recalled' || batch.status === 'closed'
                      ? ` · ${t('activation.field.batchBlocked')}`
                      : ''}
                  </option>
                ))}
              </select>
              <span className={HINT}>{t('activation.field.batchHint')}</span>
            </label>

            <label className="block">
              <span className={LABEL}>{t('activation.field.line')}</span>
              <input
                className={FIELD}
                value={lineId}
                onChange={(event) => { setLineId(event.target.value); }}
              />
              <span className={HINT}>{t('activation.field.lineHint')}</span>
            </label>
          </div>
        </CardBody>
      </Card>

      <RangeCard
        batchId={batchId}
        lineId={lineId}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: activationKeys.all });
        }}
      />
      <LineCard batchId={batchId} lineId={lineId} />
      <ExceptionsCard batchId={batchId} />
    </div>
  );
}

// ═══════════════════════════════════ modo A · por rango, con excepciones

function RangeCard({
  batchId,
  lineId,
  onDone,
}: {
  batchId: number | null;
  lineId: string;
  onDone: () => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [key, setKey] = useState<string | null>(null);
  // Arranca SIN elegir. Que `none` fuera el valor por defecto convertiría la
  // firma de "el rollo se aplicó entero" en algo que se acepta por no tocar
  // nada — que es exactamente la omisión que #80 viene a cerrar.
  const [mode, setMode] = useState<ExceptionMode | null>(null);
  const [reason, setReason] = useState<VoidReason>('damaged');
  const [note, setNote] = useState('');
  const [detail, setDetail] = useState('');

  const desde = toNumber(from);
  const hasta = toNumber(to);
  const ancho = rangeWidth(desde, hasta);
  const clave = key ?? suggestBatchKey(lineId, 1, new Date());

  const tramos = useMemo(() => parseRanges(detail), [detail]);
  const lecturas = useMemo(() => parseScan(detail), [detail]);
  // Sin `useMemo`: son unos pocos tramos y el compilador de React no puede
  // preservar una memoización cuyas dependencias son valores derivados.
  const fuera =
    desde === null || hasta === null ? [] : outsideRange(tramos.ranges, desde, hasta);

  const send = useMutation<RangeResult, ApiError, RangeBody>({
    mutationFn: (body) => api.post<RangeResult>('/api/v1/activations/range', body),
    onSuccess: onDone,
  });

  const declaracionLista =
    mode === 'none' ||
    (mode === 'ranges' && tramos.ranges.length > 0 && fuera.length === 0) ||
    (mode === 'codes' && lecturas.codes.length > 0);
  const motivoListo = mode !== 'ranges' && mode !== 'codes'
    ? true
    : reason !== 'other' || note.trim().length > 0;
  const listo =
    batchId !== null && ancho > 0 && clave.trim().length > 0 && declaracionLista && motivoListo;

  function exceptions(): ExceptionSpec {
    if (mode !== 'ranges' && mode !== 'codes') return { none: true };
    const nota = note.trim().length > 0 ? note.trim() : null;
    return mode === 'ranges'
      ? { reason, note: nota, ranges: tramos.ranges }
      : { reason, note: nota, codes: lecturas.codes };
  }

  function submit(): void {
    // `listo` ya garantiza `batchId !== null`: TypeScript lo narra solo a
    // través del alias. `desde`/`hasta` no, porque `listo` los mira a través
    // de `ancho`.
    if (!listo || desde === null || hasta === null) return;
    send.mutate({
      client_batch_id: clave.trim(),
      batch_id: batchId,
      serial_from: desde,
      serial_to: hasta,
      exceptions: exceptions(),
      line_id: lineId.trim().length > 0 ? lineId.trim() : null,
      client_sent_at: new Date().toISOString(),
    });
  }

  const result = send.data ?? null;

  return (
    <Card>
      <CardHeader
        title={t('activation.range.title')}
        description={t('activation.range.description')}
        actions={<Badge tone="neutral">{t('activation.modes.badge.range')}</Badge>}
      />
      <CardBody className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className={LABEL}>{t('activation.range.from')}</span>
            <input
              className={`${FIELD} font-mono`}
              inputMode="numeric"
              value={from}
              onChange={(event) => { setFrom(event.target.value); }}
            />
          </label>
          <label className="block">
            <span className={LABEL}>{t('activation.range.to')}</span>
            <input
              className={`${FIELD} font-mono`}
              inputMode="numeric"
              value={to}
              onChange={(event) => { setTo(event.target.value); }}
            />
          </label>
          <label className="block">
            <span className={LABEL}>{t('activation.field.key')}</span>
            <input
              className={`${FIELD} font-mono`}
              value={clave}
              onChange={(event) => { setKey(event.target.value); }}
            />
          </label>
        </div>
        <p className="text-xs text-ink-subtle">
          {t('activation.range.width', { count: fmt.number(ancho) })}
        </p>

        {/* ── la parte que no se puede omitir ───────────────────────────── */}
        <fieldset className="rounded border border-border bg-surface-sunken p-3">
          <legend className="px-1 text-xs tracking-wide text-ink-subtle uppercase">
            {t('activation.exceptions.legend')}
          </legend>
          <p className="mb-2 max-w-prose text-xs text-ink-muted">
            {t('activation.exceptions.rule')}
          </p>
          <div className="flex flex-wrap gap-4">
            {(['none', 'ranges', 'codes'] as const).map((option) => (
              <label key={option} className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="exception-mode"
                  value={option}
                  checked={mode === option}
                  onChange={() => { setMode(option); }}
                />
                {t(EXCEPTION_MODE_LABELS[option])}
              </label>
            ))}
          </div>

          {(mode === 'ranges' || mode === 'codes') && (
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <ReasonField reason={reason} note={note} onReason={setReason} onNote={setNote} />
              </div>
              <label className="block">
                <span className={LABEL}>
                  {mode === 'ranges'
                    ? t('activation.exceptions.ranges')
                    : t('activation.exceptions.codes')}
                </span>
                <textarea
                  className={AREA}
                  spellCheck={false}
                  value={detail}
                  placeholder={
                    mode === 'ranges'
                      ? t('activation.exceptions.rangesPlaceholder')
                      : t('activation.field.codesPlaceholder')
                  }
                  onChange={(event) => { setDetail(event.target.value); }}
                />
                <span className={HINT}>
                  {mode === 'ranges'
                    ? t('activation.exceptions.rangesHint', {
                        tramos: fmt.number(tramos.ranges.length),
                        count: fmt.number(tramos.units),
                      })
                    : t('activation.scan.ready', { count: fmt.number(lecturas.codes.length) })}
                </span>
              </label>
              {mode === 'ranges' && tramos.invalid.length > 0 && (
                <p className="text-xs text-critical">
                  {t('activation.exceptions.invalidRanges', {
                    list: tramos.invalid.slice(0, 3).join(' · '),
                  })}
                </p>
              )}
              {fuera.length > 0 && (
                <p className="text-xs text-critical">{t('activation.exceptions.outside')}</p>
              )}
            </div>
          )}
        </fieldset>

        {send.isError && <ErrorState error={send.error} />}

        {result !== null && (
          <div className="space-y-3 rounded border border-border p-3">
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure label={t('activation.range.inRange')} value={fmt.number(result.in_range)} />
              <Figure
                label={t('activation.result.activated')}
                value={fmt.number(result.activated)}
                tone="verified"
              />
              <Figure
                label={t('activation.exceptions.voided')}
                value={fmt.number(result.voided)}
                tone={result.voided > 0 ? 'critical' : 'neutral'}
              />
              <Figure
                label={t('activation.result.rejected')}
                value={fmt.number(result.rejected)}
                tone={result.rejected > 0 ? 'critical' : 'neutral'}
              />
            </dl>
            <OutcomeBreakdown outcomes={result.outcomes} />
            <p className="text-xs text-ink-subtle">
              {t('activation.result.elapsed', { ms: fmt.number(Math.round(result.elapsed_ms)) })}{' '}
              {t('activation.range.exceptionRef', { id: fmt.number(result.exception_id) })}
            </p>
          </div>
        )}
      </CardBody>
      <CardBody className="flex flex-wrap items-center gap-3 border-t border-border">
        <Button variant="primary" disabled={!listo || send.isPending} onClick={submit}>
          {send.isPending ? t('activation.submitting') : t('activation.range.submit')}
        </Button>
        <p className="text-xs text-ink-subtle">{t('activation.range.order')}</p>
      </CardBody>
    </Card>
  );
}

// ═════════════════════════════════════════════ modo C · en línea, por unidad

function LineCard({
  batchId,
  lineId,
}: {
  batchId: number | null;
  lineId: string;
}): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();

  const [code, setCode] = useState('');
  const [latencies, setLatencies] = useState<number[]>([]);
  const [last, setLast] = useState<UnitResult | null>(null);

  const send = useMutation<UnitResult, ApiError, UnitBody>({
    mutationFn: async (body) => {
      const started = performance.now();
      const result = await api.post<UnitResult>('/api/v1/activations/unit', body);
      // El reloj del NAVEGADOR y no `elapsed_ms`: a la línea le importa el
      // tiempo de ida y vuelta, que es el que decide si la cinta espera.
      setLatencies((previous) =>
        [performance.now() - started, ...previous].slice(0, LATENCY_WINDOW),
      );
      return result;
    },
    onSuccess: (result) => {
      setLast(result);
      setCode('');
    },
  });

  const lecturas = useMemo(() => parseScan(code), [code]);
  const listo = batchId !== null && lecturas.codes.length > 0;
  const p50 = median(latencies);
  const ultimo = last?.units[0]?.outcome ?? null;
  const ultimoLabel = ultimo === null ? null : voidOutcomeLabelKey(ultimo);

  function submit(): void {
    const unico = lecturas.codes[0];
    if (batchId === null || unico === undefined) return;
    const linea = lineId.trim().length > 0 ? lineId.trim() : null;
    send.mutate(
      lecturas.codes.length === 1
        ? { batch_id: batchId, code: unico, line_id: linea }
        : { batch_id: batchId, codes: lecturas.codes, line_id: linea },
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('activation.line.title')}
        description={t('activation.line.description')}
        actions={<Badge tone="neutral">{t('activation.modes.badge.line')}</Badge>}
      />
      <CardBody className="space-y-3">
        <label className="block">
          <span className={LABEL}>{t('activation.line.code')}</span>
          <input
            className={`${FIELD} font-mono`}
            value={code}
            spellCheck={false}
            placeholder={t('activation.field.codesPlaceholder')}
            onChange={(event) => { setCode(event.target.value); }}
            onKeyDown={(event) => {
              // El escáner de la línea manda Enter al final de la lectura.
              if (event.key === 'Enter' && listo && !send.isPending) submit();
            }}
          />
          <span className={HINT}>{t('activation.line.codeHint')}</span>
        </label>

        {send.isError && <ErrorState error={send.error} />}

        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={latencies.length > 0 && p50 < 200 ? 'verified' : 'neutral'}>
            {t('activation.line.p50', { ms: fmt.number(Math.round(p50)) })}
          </Badge>
          <Badge tone="neutral">
            {t('activation.line.samples', { count: fmt.number(latencies.length) })}
          </Badge>
          {ultimo !== null && (
            <Badge tone={ultimo === 'activated' ? 'verified' : 'critical'}>
              {ultimoLabel === null ? ultimo : t(ultimoLabel)}
            </Badge>
          )}
        </div>
        <p className="max-w-prose text-xs text-ink-muted">{t('activation.line.budget')}</p>
      </CardBody>
      <CardBody className="flex flex-wrap items-center gap-3 border-t border-border">
        <Button variant="primary" disabled={!listo || send.isPending} onClick={submit}>
          {send.isPending ? t('activation.submitting') : t('activation.line.submit')}
        </Button>
        <p className="text-xs text-ink-subtle">{t('activation.line.failSafe')}</p>
      </CardBody>
    </Card>
  );
}

// ══════════════════════════════════ excepciones sueltas (#80)

function ExceptionsCard({ batchId }: { batchId: number | null }): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();

  const [byRange, setByRange] = useState(false);
  const [reason, setReason] = useState<VoidReason>('damaged');
  const [note, setNote] = useState('');
  const [detail, setDetail] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const lecturas = useMemo(() => parseScan(detail), [detail]);
  const desde = toNumber(from);
  const hasta = toNumber(to);
  const ancho = rangeWidth(desde, hasta);

  const send = useMutation<ExceptionResult, ApiError, ExceptionBody>({
    mutationFn: (body) => api.post<ExceptionResult>('/api/v1/activations/exceptions', body),
  });

  const listo =
    (reason !== 'other' || note.trim().length > 0) &&
    (byRange ? ancho > 0 : lecturas.codes.length > 0);
  const result = send.data ?? null;

  function submit(): void {
    if (!listo) return;
    const base: ExceptionBody = {
      reason,
      note: note.trim().length > 0 ? note.trim() : null,
      batch_id: batchId,
    };
    send.mutate(
      byRange && desde !== null && hasta !== null
        ? { ...base, serial_from: desde, serial_to: hasta }
        : { ...base, codes: lecturas.codes },
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('activation.exceptions.title')}
        description={t('activation.exceptions.description')}
        actions={<Badge tone="suspect">{t('activation.modes.badge.exceptions')}</Badge>}
      />
      <CardBody className="space-y-3">
        <p className="max-w-prose text-sm text-ink-muted">
          {t('activation.exceptions.terminal')}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <ReasonField reason={reason} note={note} onReason={setReason} onNote={setNote} />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={byRange}
            onChange={(event) => { setByRange(event.target.checked); }}
          />
          {t('activation.exceptions.byRange')}
        </label>

        {byRange ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className={LABEL}>{t('activation.range.from')}</span>
              <input
                className={`${FIELD} font-mono`}
                inputMode="numeric"
                value={from}
                onChange={(event) => { setFrom(event.target.value); }}
              />
            </label>
            <label className="block">
              <span className={LABEL}>{t('activation.range.to')}</span>
              <input
                className={`${FIELD} font-mono`}
                inputMode="numeric"
                value={to}
                onChange={(event) => { setTo(event.target.value); }}
              />
            </label>
          </div>
        ) : (
          <label className="block">
            <span className={LABEL}>{t('activation.exceptions.codes')}</span>
            <textarea
              className={AREA}
              spellCheck={false}
              value={detail}
              placeholder={t('activation.field.codesPlaceholder')}
              onChange={(event) => { setDetail(event.target.value); }}
            />
            <span className={HINT}>
              {t('activation.scan.ready', { count: fmt.number(lecturas.codes.length) })}
            </span>
          </label>
        )}

        {send.isError && <ErrorState error={send.error} />}

        {result !== null && (
          <div className="space-y-3 rounded border border-border p-3">
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Figure
                label={t('activation.result.requested')}
                value={fmt.number(result.exception.requested)}
              />
              <Figure
                label={t('activation.exceptions.voided')}
                value={fmt.number(result.exception.voided)}
              />
              <Figure
                label={t('activation.result.rejected')}
                value={fmt.number(result.exception.rejected)}
                tone={result.exception.rejected > 0 ? 'critical' : 'neutral'}
              />
            </dl>
            <ul className="flex flex-wrap gap-2">
              {Object.entries(result.outcomes)
                .filter(([, total]) => total > 0)
                .map(([outcome, total]) => {
                  const key = voidOutcomeLabelKey(outcome);
                  return (
                    <li key={outcome}>
                      <Badge tone={voidOutcomeTone(outcome)}>
                        {key === null ? outcome : t(key)}
                        <span className="font-mono">{fmt.number(total)}</span>
                      </Badge>
                    </li>
                  );
                })}
            </ul>
            {result.rejects.length > 0 && (
              <Table caption={t('activation.exceptions.rejects')}>
                <thead>
                  <tr>
                    <Th>{t('activation.rejects.column.code')}</Th>
                    <Th>{t('activation.rejects.column.outcome')}</Th>
                    <Th>{t('activation.rejects.column.existingBatch')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.rejects.map((reject) => {
                    const key = voidOutcomeLabelKey(reject.outcome);
                    return (
                      <tr key={reject.unit_id}>
                        <Td className="font-mono">
                          {reject.code === null ? `#${reject.unit_id}` : shortCode(reject.code)}
                        </Td>
                        <Td>
                          <Badge tone={voidOutcomeTone(reject.outcome)}>
                            {key === null ? reject.outcome : t(key)}
                          </Badge>
                        </Td>
                        <Td className="text-ink-muted">{reject.existing_batch_id ?? '—'}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </CardBody>
      <CardBody className="flex flex-wrap items-center gap-3 border-t border-border">
        <Button variant="primary" disabled={!listo || send.isPending} onClick={submit}>
          {send.isPending ? t('activation.submitting') : t('activation.exceptions.submit')}
        </Button>
      </CardBody>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────── piezas chicas

/** Motivo + nota. Los dos formularios de anulación piden exactamente esto. */
function ReasonField({
  reason,
  note,
  onReason,
  onNote,
}: {
  reason: VoidReason;
  note: string;
  onReason: (value: VoidReason) => void;
  onNote: (value: string) => void;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <label className="block">
        <span className={LABEL}>{t('activation.exceptions.reason')}</span>
        <select
          className={FIELD}
          value={reason}
          onChange={(event) => { onReason(event.target.value as VoidReason); }}
        >
          {VOID_REASONS.map((value) => (
            <option key={value} value={value}>
              {t(reasonLabelKey(value))}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className={LABEL}>{t('activation.exceptions.note')}</span>
        <input
          className={FIELD}
          value={note}
          onChange={(event) => { onNote(event.target.value); }}
        />
        <span className={HINT}>{t('activation.exceptions.noteHint')}</span>
      </label>
    </>
  );
}

/** Un número grande con su rótulo. Es lo que se mira de lejos en la línea. */
function Figure({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'verified' | 'critical';
}): ReactNode {
  const color =
    tone === 'verified' ? 'text-verified' : tone === 'critical' ? 'text-critical' : 'text-ink';
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className={`mt-0.5 font-mono text-xl font-semibold ${color}`}>{value}</dd>
    </div>
  );
}
